// View-only fields. Existing API metrics/totals/exports are not recalculated.
import { desktopColumnLabel } from "./desktopInformation";
export type StoreOverviewLanguage = "tr" | "en" | "ru" | "uz";
export type StoreOverviewColumn = { key: string; label: string; width: number; group: number };
const copy = {
  tr: ["Mağaza", "Hedef", "Satış", "Hedef %", "Kalan Hedef", "Günlük Gereken", "LY Satış", "LY Değişim %", "CR %", "UPT", "Ziyaretçi", "Müşteri", "Adet", "Sepet", "Birim Fiyat", "SATIŞ", "KPI", "EK BİLGİLER", "Beklenen", "", "Ay sonuna kalan", "gün", "LY: geçen yılın aynı dönemi", "Günlük Gereken: kalan hedef / kalan gün", "Veri yok", "Sırala", "mağaza"],
  en: ["Store", "Target", "Sales", "Target %", "Remaining Target", "Required / Day", "LY Sales", "LY Change %", "CR %", "UPT", "Visitors", "Customers", "Units", "Basket", "Unit Price", "SALES", "KPI", "ADDITIONAL INFO", "Expected", "", "Days to month end", "days", "LY: same period last year", "Required / Day: remaining target / remaining days", "No data", "Sort", "stores"],
  ru: ["Магазин", "План", "Продажи", "План %", "Остаток плана", "Нужно в день", "Продажи LY", "Изменение LY %", "CR %", "UPT", "Посетители", "Покупатели", "Шт.", "Средний чек", "Цена за шт.", "ПРОДАЖИ", "KPI", "ДОПОЛНИТЕЛЬНО", "Ожидается", "", "До конца месяца", "дн.", "LY: тот же период прошлого года", "Нужно в день: остаток плана / оставшиеся дни", "Нет данных", "Сортировать", "магазинов"],
  uz: ["Do‘kon", "Reja", "Savdo", "Reja %", "Qolgan reja", "Kunlik kerak", "LY savdo", "LY o‘zgarish %", "CR %", "UPT", "Tashriflar", "Mijozlar", "Dona", "O‘rtacha chek", "Dona narxi", "SAVDO", "KPI", "QO‘SHIMCHA", "Kutilgan", "", "Oy oxirigacha", "kun", "LY: o‘tgan yilning shu davri", "Kunlik kerak: qolgan reja / qolgan kun", "Ma’lumot yo‘q", "Saralash", "do‘kon"],
} as const;
export function storeOverviewCopy(language: string) {
  return copy[language as StoreOverviewLanguage] ?? copy.tr;
}
const restoredCopy = {
  tr: ["Ciro Payı %", "LY UPT", "UPT Değişim %"],
  en: ["Revenue Share %", "LY UPT", "UPT Change %"],
  ru: ["Доля выручки %", "UPT LY", "Изменение UPT %"],
  uz: ["Tushum ulushi %", "LY UPT", "UPT o‘zgarishi %"],
} as const;
export const storeOverviewOptionalPairs: Record<string, string> = { lyReceipts: "receiptsChange", lyUnits: "unitsChange", lyUnitPrice: "unitPriceChange" };
export function storeOverviewOptionalKeys(): string[] {
  return Object.entries(storeOverviewOptionalPairs).flat();
}
export function storeOverviewDefaultHiddenColumns(): Set<string> {
  return new Set(storeOverviewOptionalKeys());
}
export function storeOverviewToggleKeys(key: string): string[] {
  return storeOverviewOptionalPairs[key] ? [key, storeOverviewOptionalPairs[key]] : [key];
}
export function storeOverviewMenuColumns(language: string): StoreOverviewColumn[] {
  const companions = new Set(Object.values(storeOverviewOptionalPairs));
  return storeOverviewColumns(language).filter(column => !companions.has(column.key));
}
export function storeOverviewColumns(language: string, compact = false): StoreOverviewColumn[] {
  const c = storeOverviewCopy(language);
  const restored = restoredCopy[language as StoreOverviewLanguage] ?? restoredCopy.tr;
  const columns: StoreOverviewColumn[] = [
    { key: "label", label: c[0], width: 250, group: 0 },
    { key: "target", label: c[1], width: 166, group: 1 },
    { key: "sales", label: c[2], width: 166, group: 1 },
    { key: "targetRate", label: c[3], width: 130, group: 1 },
    { key: "remaining", label: c[4], width: 166, group: 1 },
    { key: "lySales", label: c[6], width: 166, group: 1 },
    { key: "salesChange", label: c[7], width: 134, group: 1 },
    { key: "cr", label: c[8], width: 80, group: 2 },
    { key: "upt", label: c[9], width: 80, group: 2 },
    { key: "lyUpt", label: restored[1], width: 114, group: 2 },
    { key: "uptChange", label: restored[2], width: 134, group: 2 },
    { key: "basket", label: c[13], width: 176, group: 3 },
    { key: "unitPrice", label: c[14], width: 114, group: 3 },
    { key: "visitors", label: c[10], width: 104, group: 3 },
    { key: "receipts", label: c[11], width: 92, group: 3 },
    { key: "units", label: c[12], width: 100, group: 3 },
    { key: "share", label: restored[0], width: 94, group: 3 },
  ];
  // Fit the normal 1920 px page with its existing 264 px sidebar still open.
  // These are layout widths only; values and metric definitions do not change.
  const compactWidths = [200, 120, 122, 130, 140, 124, 88, 54, 54, 120, 96, 90, 90, 76, 76, 64, 82];
  const sized = compact ? columns.map((column, index) => ({ ...column, width: compactWidths[index] })) : columns;
  const optionalCopy: Record<string, string[]> = {
    tr: ["LY Müşteri", "Müşteri Değişim %", "LY Adet", "Adet Değişim %", "LY Birim Fiyat", "Birim Fiyat Değişim %"],
    en: ["LY Customers", "Customer Change %", "LY Units", "Unit Change %", "LY Unit Price", "Unit Price Change %"],
    ru: ["Покупатели LY", "Изменение покупателей %", "Шт. LY", "Изменение количества %", "Цена за шт. LY", "Изменение цены %"],
    uz: ["LY mijozlar", "Mijozlar o‘zgarishi %", "LY dona", "Dona o‘zgarishi %", "LY dona narxi", "Dona narxi o‘zgarishi %"],
  };
  const labels = optionalCopy[language] ?? optionalCopy.tr;
  const after: Record<string, StoreOverviewColumn[]> = {};
  for (const [index, [key, comparison]] of Object.entries(storeOverviewOptionalPairs).entries()) {
    after[["receipts", "units", "unitPrice"][index]] = [
      { key, label: labels[index * 2], width: compact ? 150 : 166, group: 3 },
      { key: comparison, label: labels[index * 2 + 1], width: compact ? 96 : 134, group: 3 },
    ];
  }
  return sized.flatMap(column => [column, ...(after[column.key] ?? [])]).map(column => ({ ...column, label: desktopColumnLabel(column.key, column.label, language) }));
}
export type StoreOverviewLayoutColumn = StoreOverviewColumn & { comparison?: StoreOverviewColumn };
export function stackStoreOverviewColumns(columns: StoreOverviewColumn[]): StoreOverviewLayoutColumn[] {
  const companions: Record<string, string> = { lySales: "salesChange", lyUpt: "uptChange", ...storeOverviewOptionalPairs };
  const byKey = new Map(columns.map(column => [column.key, column]));
  const stackedKeys = new Set(Object.entries(companions).filter(([key, comparison]) => byKey.has(key) && byKey.has(comparison)).map(([, comparison]) => comparison));
  return columns.filter(column => !stackedKeys.has(column.key)).map(column => ({ ...column, comparison: byKey.get(companions[column.key]) }));
}
export function storeOverviewComparisonLabel(language: string) {
  const labels: Record<string, string> = { tr: "Değişim %", en: "Change %", ru: "Изменение %", uz: "O‘zgarish %" };
  return labels[language] ?? labels.tr;
}
// Same remaining/daily definition as the existing ExecutiveInsightPanel,
// evaluated against the selected report end, never the computer's current date.
export function storeTargetRemainder(target: number | null, sales: number, asOfDate?: string, fullMonthTarget = true) {
  const match = asOfDate?.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  const daysInMonth = match ? new Date(Date.UTC(Number(match[1]), Number(match[2]), 0)).getUTCDate() : 0;
  const elapsedDay = match ? Number(match[3]) : 0;
  const remainingDays = fullMonthTarget ? Math.max(daysInMonth - elapsedDay, 0) : 0;
  const remaining = target != null && target > 0 ? Math.max(target - sales, 0) : null;
  return { remaining, daily: remaining == null || !match ? null : remainingDays > 0 ? remaining / remainingDays : remaining, remainingDays };
}
